import { Link, useLoaderData, useLocation } from '@remix-run/react';
import { useEffect, useState } from 'react';

import { ServiceWorkerRegistration } from '~/components/ServiceWorkerRegistration';
import { UserDropdown } from '~/components/UserDropdown';
import { DayOperationsModal } from '~/components/modals/DayOperationsModal';
import { NetworkInviteModal } from '~/components/modals/NetworkInviteModal';
import { useSupport } from '~/components/support/SupportContext';
import { OfficeSwitcher } from '~/components/ui/OfficeSwitcher';
import { ProfileCompletenessBanner } from '~/components/ui/ProfileCompletenessBanner';
import type { ProfileCompletionTarget } from '~/utils/profile-completeness';
import { canManageOffices, isOwner } from '~/utils/role-helpers';

import { DemoBanner } from '../demo/DemoBanner';
import { DemoTour } from '../demo/DemoTour';
import { Button, ButtonLink } from '../ui/Button';

import { GlobalSearch } from './GlobalSearch';
import { Sidebar } from './Sidebar';
import type { SearchAction } from './global-search-catalog';

interface AuthenticatedLayoutProps {
  children: React.ReactNode;
}

export function AuthenticatedLayout({ children }: AuthenticatedLayoutProps) {
  const {
    firstName,
    lastName,
    fullName,
    companyLogo,
    companyName,
    userRole,
    userRoleRaw,
    profilePicture,
    email,
    isSuperAdmin,
    canInviteNetwork,
    userOffices = [],
    currentOfficeId = '',
    currentOfficeName = '',
    mainOfficeId = '',
    profileCompleteness,
    profileCompletionTarget,
    isDemoSession,
    isReadOnlyDemoSession,
    demoTourRequested,
    unreadNotificationCount = 0,
    newLeadsCount = 0,
    pendingSignatureCount = 0,
    showLeadsNav = true,
  } = useLoaderData<{
    firstName: string;
    lastName: string;
    fullName: string;
    companyLogo?: string;
    companyName?: string;
    userRole: string;
    userRoleRaw?: string | null;
    profilePicture?: string | null;
    email: string;
    isSuperAdmin?: boolean;
    canInviteNetwork?: boolean;
    userOffices?: Array<{ id: string; name: string }>;
    currentOfficeId?: string;
    currentOfficeName?: string;
    mainOfficeId?: string;
    profileCompleteness?: number | null;
    profileCompletionTarget?: ProfileCompletionTarget | null;
    isDemoSession?: boolean;
    isReadOnlyDemoSession?: boolean;
    demoTourRequested?: boolean;
    unreadNotificationCount?: number;
    newLeadsCount?: number;
    pendingSignatureCount?: number;
    showLeadsNav?: boolean;
  }>();
  const canManageTeamProfiles = userRoleRaw ? canManageOffices(userRoleRaw) : false;
  const canManageBilling = userRoleRaw ? isOwner(userRoleRaw) : false;

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isDayOpsModalOpen, setIsDayOpsModalOpen] = useState(false);
  const [isNetworkInviteModalOpen, setIsNetworkInviteModalOpen] = useState(false);
  const [officeSwitcherSignal, setOfficeSwitcherSignal] = useState(0);
  const location = useLocation();
  const { openSupport } = useSupport();

  const onSearchAction = (action: SearchAction) => {
    if (action === 'invite-network') setIsNetworkInviteModalOpen(true);
    if (action === 'day-prep') {
      window.history.pushState({ dayOpsModal: true }, '', location.pathname + location.search);
      setIsDayOpsModalOpen(true);
    }
    if (action === 'switch-office') setOfficeSwitcherSignal(signal => signal + 1);
    if (action === 'support') openSupport();
  };

  // Close Day Ops modal when user hits browser Back (we push state when opening)
  useEffect(() => {
    const handlePopState = () => {
      if (isDayOpsModalOpen) setIsDayOpsModalOpen(false);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDayOpsModalOpen]);

  // Determine the current page title based on the location
  const getPageTitle = () => {
    if (location.pathname.startsWith('/ops/console')) return 'Admin';
    if (location.pathname.startsWith('/notifications')) return 'Notifications';
    if (location.pathname.startsWith('/appointments')) return 'Appointments';
    if (location.pathname.startsWith('/analytics')) return 'Analytics';
    if (location.pathname.startsWith('/office-management')) return 'Offices';
    if (location.pathname.startsWith('/offices')) {
      const officeSub = location.pathname.split('/')[3];
      const embedSub = location.pathname.split('/')[4];
      if (officeSub === 'embed' && embedSub === 'referral') return 'Referral Form';
      if (officeSub === 'embed') return 'Website Lead Forms';
      if (officeSub === 'referral-access') return 'Referral Access';
      if (!officeSub && location.pathname.split('/')[2]) return 'Office Details';
      return 'Offices';
    }
    if (location.pathname.startsWith('/team')) return 'Dental Team';
    if (location.pathname.startsWith('/membership-plans')) return 'Memberships';
    if (location.pathname === '/company-settings/my-settings') return 'My Settings';
    if (location.pathname.startsWith('/company-settings')) return 'Settings';
    if (location.pathname.startsWith('/referrals/incoming')) return 'Incoming Referrals';
    if (location.pathname.startsWith('/referrals/outgoing')) return 'Outgoing Referrals';
    if (location.pathname.startsWith('/referrals/completed')) return 'Cases Completed';
    if (location.pathname.startsWith('/billing')) return 'Billing';
    if (location.pathname.startsWith('/subscription')) return 'Manage Subscription';
    if (location.pathname.startsWith('/notes')) return 'Notes';
    if (location.pathname.startsWith('/doctors')) return 'Doctors';
    return 'Referrals';
  };

  return (
    <div className="flex min-h-screen flex-col">
      {isReadOnlyDemoSession && <DemoBanner isDemoSession={!!isDemoSession} />}
      <DemoTour enabled={!!isReadOnlyDemoSession} tourRequested={!!demoTourRequested} />
      <div className="flex flex-1 min-h-0">
        <Sidebar
          companyName={companyName}
          companyLogo={companyLogo}
          mainOfficeId={mainOfficeId}
          unreadNotificationCount={unreadNotificationCount}
          newLeadsCount={newLeadsCount}
          showLeadsNav={showLeadsNav}
          canManageTeamProfiles={canManageTeamProfiles}
          canManageBilling={canManageBilling}
          isSuperAdmin={!!isSuperAdmin}
        />

        {/* Main content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <header className="bg-white border-b border-gray-200">
            <div className="flex flex-col gap-2 px-6 py-4">
              <div className="flex items-center justify-between gap-2">
                <h1 className="shrink-0 text-2xl font-semibold text-gray-900">{getPageTitle()}</h1>
                <div className="flex shrink-0 items-center gap-2">
                  <OfficeSwitcher
                    offices={userOffices}
                    currentOfficeId={currentOfficeId}
                    currentOfficeName={currentOfficeName}
                    openSignal={officeSwitcherSignal}
                  />
                  {pendingSignatureCount > 0 && (
                    <Link
                      to="/signature-queue"
                      aria-label={`Signature queue, ${pendingSignatureCount} pending`}
                      className="relative inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      Signatures
                      <span
                        data-testid="signature-badge"
                        className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-600 px-1.5 text-xs font-semibold text-white"
                      >
                        {pendingSignatureCount > 99 ? '99+' : pendingSignatureCount}
                      </span>
                    </Link>
                  )}
                  <Button
                    onClick={() => {
                      window.history.pushState(
                        { dayOpsModal: true },
                        '',
                        location.pathname + location.search
                      );
                      setIsDayOpsModalOpen(true);
                    }}
                    size="md"
                    variant="outline"
                  >
                    Provider's Day-Prep Station
                  </Button>
                  {canInviteNetwork !== false && (
                    <Button
                      onClick={() => setIsNetworkInviteModalOpen(true)}
                      size="md"
                      variant="outline"
                    >
                      Invite your Network
                    </Button>
                  )}
                  <ButtonLink to="/referrals/new" size="md">
                    New Referral
                  </ButtonLink>
                  <div className="flex items-center">
                    <UserDropdown
                      isOpen={isDropdownOpen}
                      onClose={() => setIsDropdownOpen(!isDropdownOpen)}
                      userName={fullName}
                      firstName={firstName}
                      lastName={lastName}
                      userRole={userRole}
                      userAvatar={profilePicture}
                      userEmail={email}
                    />
                  </div>
                </div>
              </div>
              <GlobalSearch
                mainOfficeId={mainOfficeId}
                canManageBilling={canManageBilling}
                canManageTeamProfiles={canManageTeamProfiles}
                isSuperAdmin={!!isSuperAdmin}
                currentOfficeName={currentOfficeName}
                officeCount={userOffices.length}
                onAction={onSearchAction}
              />
            </div>
          </header>

          {/* Main content area */}
          <main className="p-6 bg-gray-50 flex-1">
            {profileCompleteness != null && !isReadOnlyDemoSession ? (
              <ProfileCompletenessBanner
                percent={profileCompleteness}
                target={profileCompletionTarget ?? null}
              />
            ) : null}
            {children}
          </main>
        </div>

        {/* Service Worker Registration for Push Notifications */}
        <ServiceWorkerRegistration />

        {/* Network Invite Modal */}
        <NetworkInviteModal
          isOpen={isNetworkInviteModalOpen}
          setIsOpen={setIsNetworkInviteModalOpen}
        />

        {/* Day to Day Operations Modal */}
        <DayOperationsModal
          isOpen={isDayOpsModalOpen}
          onClose={() => setIsDayOpsModalOpen(false)}
        />
      </div>
    </div>
  );
}
